export interface StreamedCsvOptions {
	/** Rewrite just the header line (first line) of the CSV before it's piped through. */
	rewriteHeader?: (headerLine: string) => string
	/** Error message thrown when the body holds only whitespace, or a header and no rows. */
	emptyMessage?: string
}

/**
 * Message for a failed (non-ok) export response: the backend's reason on a 4xx, otherwise a plain
 * fallback. A 5xx body is upstream detail (timeouts, connection errors) that a user can't act on.
 */
export async function getExportErrorMessage(response: Response, action = 'Export'): Promise<string> {
	const fallback = `${action} failed`
	if (response.status >= 500) {
		return fallback
	}
	let text: string
	try {
		text = await response.text()
	} catch {
		return fallback
	}

	if (!text.trim()) {
		return fallback
	}

	try {
		const parsed = JSON.parse(text) as { error_message?: unknown; message?: unknown; error?: unknown }
		const message = parsed.error_message ?? parsed.message ?? parsed.error
		return typeof message === 'string' && message.trim() ? message : fallback
	} catch {
		// Body isn't JSON, so surface a short raw-text snippet.
		return text.length <= 200 ? text : fallback
	}
}

/**
 * Renames one column in a CSV header line, for `rewriteHeader`. Cells match unquoted; the
 * replacement is quoted when it carries a comma or quote. A trailing '\r' is left in place.
 */
export function renameCsvHeaderColumn(headerLine: string, oldKey: string, newKey: string): string {
	if (oldKey === newKey) {
		return headerLine
	}
	const carriageReturn = headerLine.endsWith('\r') ? '\r' : ''
	const line = carriageReturn ? headerLine.slice(0, -1) : headerLine
	const replacement = /[",]/.test(newKey) ? `"${newKey.replace(/"/g, '""')}"` : newKey
	const cells = line.split(',').map(cell => {
		const unquoted = cell.trim().replace(/^"(.*)"$/, '$1')
		return unquoted === oldKey ? replacement : cell
	})
	return `${cells.join(',')}${carriageReturn}`
}

const WHITESPACE_BYTES = new Set([9, 10, 13, 32])
const hasContent = (bytes: Uint8Array) => bytes.some(byte => !WHITESPACE_BYTES.has(byte))

const NEWLINE_BYTE = 10 // '\n' - a single UTF-8 byte regardless of what precedes it, so it's safe to
// search for at the byte level without decoding first (CRLF line endings still work: the trailing
// '\r' just stays part of "the header bytes", same as it would with a text-based search).

function concatBytes(chunks: Uint8Array[], totalLength: number): Uint8Array {
	const combined = new Uint8Array(totalLength)
	let offset = 0
	for (const chunk of chunks) {
		combined.set(chunk, offset)
		offset += chunk.length
	}
	return combined
}

/**
 * Wraps a fetch Response's body so the header line can be rewritten and an empty-body check
 * performed WITHOUT buffering the whole response into memory first (e.g. via `response.text()`
 * or `response.clone().text()`). Only the bytes up to the header's first newline are peeked - the
 * rest of the underlying network stream is passed straight through as raw, untouched bytes, so a
 * caller piping this into `downloadFileWithStreaming` sees real transfer progress instead of the
 * UI sitting on "Preparing..." until the entire body has already arrived.
 *
 * The peeked prefix is located and sliced at the BYTE level, not via decode-then-re-encode: only
 * the header substring itself goes through TextDecoder/TextEncoder for the rewrite, so any data
 * rows that happen to arrive in the same initial chunk(s) as the header are passed through as raw
 * bytes rather than round-tripped through UTF-8 (which would silently corrupt any byte sequence
 * that isn't strictly valid UTF-8).
 */
export async function prepareStreamedCsvResponse(
	response: Response,
	options: StreamedCsvOptions = {},
): Promise<Response> {
	const { rewriteHeader, emptyMessage = 'No data found for the selected filters' } = options
	const body = response.body
	if (!body) {
		throw new Error(emptyMessage)
	}

	const reader = body.getReader()
	const pending: Uint8Array[] = []
	let pendingLength = 0
	let newlineByteIndex = -1
	let sawData = false

	// Bounded by the header line's length in the normal case - only a body with no newline
	// anywhere (pathological/malformed CSV) reads all the way to the end here, matching the old
	// full-buffer behavior for that one edge case.
	while (true) {
		const { done, value } = await reader.read()
		if (done) {
			break
		}
		if (!value || value.length === 0) {
			continue
		}

		sawData = true
		const idxInChunk = value.indexOf(NEWLINE_BYTE)
		pending.push(value)
		if (idxInChunk !== -1) {
			newlineByteIndex = pendingLength + idxInChunk
			pendingLength += value.length
			break
		}
		pendingLength += value.length
	}

	if (!sawData) {
		reader.cancel().catch(() => {})
		throw new Error(emptyMessage)
	}

	let combined = concatBytes(pending, pendingLength)

	if (!new TextDecoder().decode(combined).trim()) {
		reader.cancel().catch(() => {})
		throw new Error(emptyMessage)
	}

	// A header with no rows under it is as empty as no body, so read on until a row starts.
	if (newlineByteIndex !== -1 && !hasContent(combined.subarray(newlineByteIndex))) {
		let sawRow = false
		while (!sawRow) {
			const { done, value } = await reader.read()
			if (done) {
				break
			}
			if (value && value.length > 0) {
				pending.push(value)
				pendingLength += value.length
				sawRow = hasContent(value)
			}
		}
		if (!sawRow) {
			reader.cancel().catch(() => {})
			throw new Error(emptyMessage)
		}
		combined = concatBytes(pending, pendingLength)
	}

	let prefixBytes: Uint8Array
	let lengthDelta = 0

	if (rewriteHeader && newlineByteIndex !== -1) {
		const decoder = new TextDecoder()
		const encoder = new TextEncoder()
		const originalHeaderBytes = combined.subarray(0, newlineByteIndex)
		const rewrittenHeaderBytes = encoder.encode(rewriteHeader(decoder.decode(originalHeaderBytes)))
		lengthDelta = rewrittenHeaderBytes.length - originalHeaderBytes.length

		const rest = combined.subarray(newlineByteIndex) // from '\n' onward - raw bytes, untouched
		prefixBytes = new Uint8Array(rewrittenHeaderBytes.length + rest.length)
		prefixBytes.set(rewrittenHeaderBytes, 0)
		prefixBytes.set(rest, rewrittenHeaderBytes.length)
	} else {
		prefixBytes = combined
	}

	const stream = new ReadableStream<Uint8Array>({
		start(controller) {
			controller.enqueue(prefixBytes)
		},
		async pull(controller) {
			const { done, value } = await reader.read()
			if (done) {
				controller.close()
				return
			}
			if (value && value.length > 0) {
				controller.enqueue(value)
			}
		},
		cancel(reason) {
			reader.cancel(reason).catch(() => {})
		},
	})

	const headers = new Headers(response.headers)
	const contentLength = Number(headers.get('Content-Length'))
	if (Number.isFinite(contentLength) && contentLength > 0) {
		headers.set('Content-Length', String(contentLength + lengthDelta))
	}

	return new Response(stream, {
		status: response.status,
		statusText: response.statusText,
		headers,
	})
}
